import { memo, ReactNode, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import { validateTenantName } from '../validation';

const useStyles = makeStyles()({
  formControl: {
    display: 'flex',
    flexDirection: 'row',
  },
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
  },
  inputElement: {
    flex: '1 1 auto',
  },
  inputElementInput: {
    height: '30px',
  },
  textBefore: {
    color: 'gray',
  },
});

const TenantTextInput = ({
  id,
  value,
  name,
  onChange,
  helperText,
  required,
  disabled,
  error,
  textBefore,
}: {
  id: string;
  value: string;
  name: string;
  onChange: (value: any) => void;
  helperText?: string | ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  textBefore?: string | null;
}) => {
  const { classes } = useStyles();
  return (
    <FormControl fullWidth variant="standard" className={classes.formControl}>
      <Box className={classes.inputBox}>
        <TextField
          id={id}
          name={name}
          label="Name"
          className={classes.inputElement}
          required={required}
          disabled={disabled}
          error={error}
          value={value}
          size="medium"
          onChange={onChange}
          onBlur={onChange}
          variant="standard"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              className: classes.inputElementInput,
              startAdornment: textBefore ? (
                <InputAdornment
                  position="start"
                  disableTypography
                  className={classes.textBefore}
                >
                  {textBefore}
                </InputAdornment>
              ) : undefined,
            },
            formHelperText: { disabled: false },
          }}
          helperText={disabled ? '' : helperText}
        />
      </Box>
    </FormControl>
  );
};

type TenantNameSectionProps = {
  value: string;
  stage: string;
  tenantId?: string;
  maxLength?: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
};

export const TenantNameSection = memo(
  ({
    value,
    stage,
    tenantId,
    maxLength,
    onChange,
    disabled,
    required = true,
  }: TenantNameSectionProps) => {
    const [nameError, setNameError] = useState('');

    const onNameChange = (e: any) => {
      setNameError(validateTenantName(e.target.value, maxLength));
      onChange(e.target.value);
    };

    return (
      <FormControl fullWidth variant="standard" required={required}>
        <TenantTextInput
          id="tenant-name"
          name="name"
          value={value ?? ''}
          required={required}
          disabled={disabled}
          onChange={onNameChange}
          textBefore={tenantId ? '' : `${stage}-${tenantId ?? '****'}-`}
          error={Boolean(nameError)}
          helperText={
            nameError || (
              <span>
                For example: {stage}-****-secrets-sync, where **** is reserved
                for tenant ID.{' '}
                {maxLength !== undefined && `At most ${maxLength} characters. `}
                <Link
                  target="_blank"
                  underline="hover"
                  href="https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation/#tenants"
                >
                  More details.
                </Link>
              </span>
            )
          }
        />
      </FormControl>
    );
  },
);
